import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AppApiStrategy } from '~/app/app-api-strategy';
import { LayoutService } from '~/app/core/services/store/layout.service';
import { ExternalFile, Purpose } from '../../external-file/types';

@Injectable({
  providedIn: 'root',
})
export class S3Service {
  #apiStrategy = AppApiStrategy;
  readonly #http = inject(HttpClient);
  readonly #layoutService = inject(LayoutService);

  public uploadFileToS3(file: Blob, purpose: Purpose): Observable<ExternalFile> {
    const url = this.#apiStrategy.getServiceUrl(['external-file', 'add', 'file']);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', purpose);

    return this.#apiStrategy.getHeaders(undefined, true, true).pipe(
      mergeMap((headers) => {
        this.#layoutService.setLoadingLocked(false);
        return this.#http.post<ExternalFile>(url, formData, { headers });
      }),
    );
  }

  public deleteFile(key: string): Observable<void> {
    const url = this.#apiStrategy.getServiceUrl(['external-file', 'delete', 'file', encodeURIComponent(key)]);
    return this.#apiStrategy.getHeaders(undefined, true, true).pipe(
      mergeMap((headers) => {
        this.#layoutService.setLoadingLocked(false);
        return this.#http.delete<void>(url, { headers });
      }),
    );
  }

  public getFileUrl(key: string): Observable<any> {
    const url = this.#apiStrategy.getServiceUrl(['external-file', 'file', encodeURIComponent(key)]);
    return this.#apiStrategy.getHeaders(undefined, true, true).pipe(
      mergeMap((headers) => {
        this.#layoutService.setLoadingLocked(false);
        return this.#http.get(url, { headers, responseType: 'blob' });
      }),
    );
  }

  public getFileLibraryUrl(purpose: Purpose, pagination: { skip: number; limit: number }): Observable<any> {
    const params = new HttpParams().set('purpose', purpose).set('skip', pagination.skip).set('limit', pagination.limit);
    const url = this.#apiStrategy.getServiceUrl(['external-file', 'library']);
    return this.#apiStrategy.getHeaders(undefined, true, true).pipe(
      mergeMap((headers) => {
        this.#layoutService.setLoadingLocked(false);
        return this.#http.get(url, { params: params, headers });
      }),
    );
  }
}
